/** @jsxImportSource @emotion/react */
import React, {useState, useEffect} from 'react'
import CycloneIcon from '@mui/icons-material/Cyclone'
import {WidgetContainer, Graphs} from 'components'
import {useDependency, useResource, useFilter} from 'hooks'
import { Widgets } from './'
import {useSavedFilters} from 'contexts/SavedFiltersContext'
import moment from 'moment-timezone'
import useAuthorization, {AccessGroups} from 'hooks/useAuthorization'
import _ from 'lodash'

const DEFAULT_FILTER = {
  gridZone: '15',
  interval: '30minute',
}

const DEFAULT_RANGE = {
  daily:      () => ({from_trading_date: moment().subtract(180, 'days'), to_trading_date: moment()}),
  monthly:    () => ({from_trading_date: moment().subtract(24, 'months'), to_trading_date: moment()}),
  '30minute': () => ({from_trading_date: moment().subtract(24, 'hours'), to_trading_date: moment()}),
}

const PAID_USER_GROUPS = [AccessGroups.EM6_SCAD, AccessGroups.EM6_CARB, AccessGroups.EM6_GWAP ]

//
// Two versions of this widget are rendered based on user permissions
// - the free version shows daily stacked generation from the /free/price endpoint
// - the paid version shows yearly/monthly/daily stacked generation from /gen_region_price
//
const StackedGenerationGraph = (props) => {
  const widget = Widgets['stacked-generation-graph']
  const [filter, setFilter] = useFilter(null, 'stackedgwap')
  const savedFilters = useSavedFilters()
  const [generation, generationActions] = useResource('generation')
  const { groups } = useAuthorization()
  const source = _.intersection(groups, PAID_USER_GROUPS).length ? 'generation' : 'generation_free'

  const [loading, result, reload] = useDependency(
    () => {
      const range = DEFAULT_RANGE[filter.interval] ? DEFAULT_RANGE[filter.interval]() : {}
      return generationActions.request(source, {grid_zone_id: filter.gridZone, interval: filter.interval, ...range})
    }, [filter])

  const updatedAt = generation[source]?.responseAt
  const [zoom, setZoom] = useState({})
  const zoomCallback = zoom => setZoom(zoom)

  useEffect(() => {
    const initialFilter = savedFilters.load(widget) || {}
    setFilter({...DEFAULT_FILTER, ...initialFilter, ...filter})
  }, [])

  return (
    <WidgetContainer
      widget={widget}
      title="Generation Quantities (MWh)"
      icon={<CycloneIcon/>}
      lastUpdatedAt={updatedAt}
      lastUpdatedAtAddMinutes={0}
      loading={loading}
      onWidgetRefresh={reload}
      renderFilter={() => <Graphs.StackedGenerationFilter source={source} filter={filter} onChange={setFilter} />}
      onFilterSave={() => savedFilters.update(widget, filter)}
      zoom={zoom}
      {...props}
    >
      <Graphs.StackedGeneration
        data={generation[source]?.data}
        source={source}
        interval={source === 'generation' ? filter.interval : 'daily'}
        zoomCallback={zoomCallback}
      />
    </WidgetContainer>
  )
}

export default StackedGenerationGraph